import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { openModal as openModalAction } from '../../../../../../actions/modal-actions';
import Spinner from '../../../../../../components/global/Spinner';
import {
  ORDER_STATUS,
  ORDER_STATUS_LABELS,
} from '../../../../../../constants/pro-custom-prints';
import withBasename from '../../../../../../components/routing/withBasename';
import { PurchasedCustomPrint } from '../../../../../../graphql/queries/pro-custom-prints';
import IdentityName from '../../../../../../components/global/identity/IdentityName';
import IdentityAvatarContainer from '../../../../../../containers/global/identity/IdentityAvatarContainer';
import { getProductBasenames } from '../../../../../../util/role-definitions';
import { formatPriceForDisplay } from '../../../../../../util/format-money';
import Button from '../../../../../../components/global/Button';
import {
  PHOTO_VIEW_MODAL,
  CUSTOM_PRINT_UPDATE_STATUS,
  TRANSACTION_DETAILS_MODAL,
} from '../../../../../../constants/modals';
import DownloadPhotoButtonContainer from '../../../../../../containers/global/DownloadPhotoButtonContainer';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import PurchasedCustomPrintUpdateStatusModal from '../components/PurchasedCustomPrintUpdateStatusModal';
import UserAddressDisplay from '../../../../../../components/global/user-address/UserAddressDisplay';
import { formatPhone } from '../../../../../../util/phone';
import PhotoViewModal from '../../../../../../components/global/modal/PhotoViewModal';
import {
  getPurchasedCustomPrintPhotoUrl,
  isWaldogram,
} from '../../../../../../util/custom-prints';
import CustomPrintPlaqueConfigurator from '../../../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/CustomPrintPlaqueConfigurator';
import {
  getPurchasedCustomPrintAccountCardPrintSize,
  mapPurchasedCustomPrintToConfiguration,
} from './helpers';
import { isAlbumCustomPrintConfigurableInPlace } from '../../../HierarchyItem/Matches/components/PurchasingOptions/helpers';
import {
  PHOTO_ITEM_TYPE,
  TRANSACTION_ITEM_TYPE,
} from '../../../../../../constants/transactions';
import Alert from '../../../../../../components/global/Alert';

const propTypes = {
  loading: PropTypes.bool,
  purchasedCustomPrint: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    total_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
    account: PropTypes.shape({
      id: PropTypes.string,
    }),
    custom_print: PropTypes.shape({
      id: PropTypes.string,
    }),
    shipping_purchased: PropTypes.bool,
  }),
  openModal: PropTypes.func.isRequired,
  basename: PropTypes.oneOf(getProductBasenames()),
  albumInformationHidden: PropTypes.bool,
};

const AlbumPurchasedCustomPrintDetailsContainer = ({
  purchasedCustomPrint,
  loading,
  basename,
  openModal,
  albumInformationHidden,
}) => {
  const {
    identity,
    account,
    photos,
    transaction,
    transaction_item: transactionItem,
  } = purchasedCustomPrint ?? {};

  const emailAddress = account?.email_address ?? transaction?.email_address;

  const orderedPhotos = useMemo(
    () =>
      [...(photos?.records ?? [])].sort((a, b) => {
        if (a.position < b.position) {
          return -1;
        }

        if (a.position < b.position) {
          return 1;
        }

        return 0;
      }),
    [purchasedCustomPrint],
  );

  const album = useMemo(() => {
    if (photos?.records?.[0]?.photo?.albums?.records?.length > 0) {
      return photos?.records?.[0]?.photo?.albums?.records?.[0];
    }

    return purchasedCustomPrint?.album;
  }, [purchasedCustomPrint]);

  const isFromPrintPackage = useMemo(() => {
    if (purchasedCustomPrint?.price?.value) {
      return false;
    }

    if (purchasedCustomPrint?.total_price?.value) {
      return false;
    }

    if (!transactionItem) {
      return false;
    }

    return (
      transactionItem?.item_type === TRANSACTION_ITEM_TYPE.PHOTO &&
      transactionItem?.photo_item_type === PHOTO_ITEM_TYPE.PRINT_PACKAGE
    );
  }, [purchasedCustomPrint, transactionItem]);

  const accountCardPrintSize = useMemo(
    () => getPurchasedCustomPrintAccountCardPrintSize(purchasedCustomPrint),
    [purchasedCustomPrint],
  );

  const printFileDownloadUrl = useMemo(
    () => purchasedCustomPrint?.print_file_download_url,
    [purchasedCustomPrint],
  );

  const itemPrice = useMemo(() => {
    if (purchasedCustomPrint?.price?.value) {
      return purchasedCustomPrint?.price?.value;
    }

    return transactionItem?.price?.value ?? 0;
  }, [purchasedCustomPrint, transactionItem]);

  const totalPrice = useMemo(() => {
    if (purchasedCustomPrint?.total_price?.value) {
      return purchasedCustomPrint?.total_price?.value;
    }

    return transactionItem?.total_price?.value ?? 0;
  }, [purchasedCustomPrint, transactionItem]);

  const shippingPrice = useMemo(() => {
    if (purchasedCustomPrint?.shipping_price?.value) {
      return purchasedCustomPrint?.shipping_price?.value;
    }

    return transactionItem?.item_shipping_price?.value ?? 0;
  }, [purchasedCustomPrint, transactionItem]);

  const shippingPurchased = useMemo(
    () =>
      purchasedCustomPrint?.shipping_purchased ||
      transactionItem?.shipping_purchased ||
      !!shippingPrice,
    [purchasedCustomPrint, transactionItem],
  );

  const onPrintFileDownload = (url = printFileDownloadUrl) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.target = '_blank';
    downloadLink.download = `${transactionItem?.id}-print.jpg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const otherPrintablePurchasedCustomPrints = useMemo(
    () =>
      transaction?.purchased_custom_prints?.records?.filter(
        ({ id, print_file_download_url: downloadUrl }) =>
          id !== purchasedCustomPrint.id && !!downloadUrl,
      ),
    [transaction],
  );

  if (!purchasedCustomPrint || loading) {
    return (
      <div className="text-center m-auto">
        <Spinner />
      </div>
    );
  }

  return (
    <Fragment>
      {!albumInformationHidden && (
        <div className="mb-4 d-flex align-items-center justify-content-center">
          <Button
            btnSize="xs"
            className="mr-auto mt-2 update-order-status"
            onClick={() => openModal(CUSTOM_PRINT_UPDATE_STATUS)}
          >
            Update Order Status
          </Button>
        </div>
      )}

      {isFromPrintPackage && (
        <Alert type="note">
          This item was purchased as part of a Print Package purchase. To see
          the transaction details
          <Button
            btnStyle="link"
            btnSize="sm"
            className="ml-1"
            onClick={() =>
              openModal(TRANSACTION_DETAILS_MODAL, { transactionItem })
            }
          >
            Click here &rarr;
          </Button>{' '}
        </Alert>
      )}

      <div className="row">
        <div className="col-lg-6 col-12">
          <table className="table table-custom-prints mb-3">
            <tbody>
              {!!purchasedCustomPrint?.custom_print?.name && (
                <tr>
                  <th className="text-left">Product</th>
                  <td className="text-right">
                    {purchasedCustomPrint?.custom_print?.name}
                  </td>
                </tr>
              )}
              {!!purchasedCustomPrint?.custom_print?.description && (
                <tr>
                  <th className="text-left">Product Description</th>
                  <td className="text-right text-white-space-normal">
                    {purchasedCustomPrint?.custom_print?.description}
                  </td>
                </tr>
              )}
              <tr>
                <th className="text-left">Purchased by</th>
                <td className="text-right">{account?.name}</td>
              </tr>
              <tr>
                <th className="text-left">Date of purchase</th>
                <td className="text-right">
                  {moment
                    .utc(purchasedCustomPrint.created_at)
                    .local()
                    .format('MMMM DD, YYYY - HH:mm')}
                </td>
              </tr>
              <tr>
                <th className="text-left">Contact info</th>
                <td className="text-right">
                  {account?.phone_number && (
                    <span className="d-block">
                      Tel: {formatPhone(account?.phone_number)}
                    </span>
                  )}
                  {emailAddress && (
                    <span className="d-block">
                      <span className="d-none d-md-inline-block">Email:</span>{' '}
                      {emailAddress}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th className="text-left">For identity</th>
                <td className="text-right">
                  <IdentityAvatarContainer
                    size={52}
                    className="mr-1 flex-shrink-0"
                    identity={identity}
                  />
                  <IdentityName identity={identity} user={account} />
                </td>
              </tr>
              {!albumInformationHidden && (
                <tr>
                  <th className="text-left">Album</th>
                  <td className="text-right">
                    <Link to={`/app/${basename}/albums/${album?.id}`}>
                      {album?.name}
                    </Link>
                  </td>
                </tr>
              )}
              <tr>
                <th className="text-left">Quantity</th>
                <td className="text-right">{purchasedCustomPrint?.quantity}</td>
              </tr>
              {!isFromPrintPackage && (
                <Fragment>
                  <tr>
                    <th className="text-left">Discounted purchase</th>
                    <td className="text-right">
                      {purchasedCustomPrint?.discount_purchased ? 'Yes' : 'No'}
                    </td>
                  </tr>
                  {(purchasedCustomPrint?.shipping_enabled ||
                    shippingPurchased) && (
                    <tr>
                      <th className="text-left">Shipping costs</th>
                      <td className="text-right">
                        ${formatPriceForDisplay(shippingPrice)}
                      </td>
                    </tr>
                  )}

                  <tr>
                    <th className="text-left">Shipping purchased</th>
                    <td className="text-right">
                      {shippingPurchased ? 'Yes' : 'No'}
                    </td>
                  </tr>
                </Fragment>
              )}

              {isWaldogram(purchasedCustomPrint) && (
                <Fragment>
                  <tr>
                    <th className="text-left">Cabin name</th>
                    <th className="text-right">
                      {purchasedCustomPrint.waldogram?.cabin_name ?? 'N/A'}
                    </th>
                  </tr>

                  <tr>
                    <th className="text-left">Recipient name</th>
                    <th className="text-right">
                      {purchasedCustomPrint.waldogram?.identity_name ?? 'N/A'}
                    </th>
                  </tr>
                </Fragment>
              )}

              {!isFromPrintPackage && (
                <Fragment>
                  {(purchasedCustomPrint?.shipping_enabled ||
                    shippingPurchased) && (
                    <tr>
                      <th className="text-left">Item price</th>
                      <td className="text-right">
                        ${formatPriceForDisplay(itemPrice)}
                      </td>
                    </tr>
                  )}

                  <tr>
                    <th className="text-left">Total price</th>
                    <td className="text-right">
                      ${formatPriceForDisplay(totalPrice)}
                    </td>
                  </tr>
                </Fragment>
              )}

              <tr>
                <th className="text-left">Order status</th>
                <td className="text-right">
                  {ORDER_STATUS_LABELS[purchasedCustomPrint?.order_status]}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="col-lg-6 col-12">
          {(shippingPurchased ||
            (purchasedCustomPrint?.shipping_enabled &&
              purchasedCustomPrint?.shipping_required)) && (
            <Fragment>
              <h5 className="mb-1">Shipping Information</h5>
              <UserAddressDisplay
                className="mb-2 mt-1"
                address={purchasedCustomPrint?.account?.addresses?.[0]}
              />
            </Fragment>
          )}

          {purchasedCustomPrint?.custom_print_caption_enabled &&
            purchasedCustomPrint?.custom_print_caption && (
              <Fragment>
                <h5 className="mb-1">User Caption</h5>
                <p>
                  <code>{purchasedCustomPrint.custom_print_caption}</code>
                </p>
              </Fragment>
            )}

          {printFileDownloadUrl && (
            <Fragment>
              <h5 className="mb-1">Configured Product</h5>
              <p>
                The image below represent the user configured product for your{' '}
                <b>{purchasedCustomPrint?.custom_print?.name}</b>, to be printed
                on a <b>{accountCardPrintSize}</b> print.
              </p>
              <div className="row">
                <div className="col-lg-4 col-6 mb-2">
                  <div className="photo-grid-card custom-print-photo">
                    {accountCardPrintSize && (
                      <div className="custom-print-photo-size-tag">
                        {accountCardPrintSize}
                      </div>
                    )}
                    <a
                      href="#"
                      onClick={event => {
                        event.preventDefault();
                        // Since the intent here is to view, we do not use the download URL
                        openModal(PHOTO_VIEW_MODAL, {
                          photoUrl: purchasedCustomPrint.print_file_url,
                        });
                      }}
                    >
                      <img src={purchasedCustomPrint.print_file_url} alt="" />
                    </a>

                    <Button
                      onClick={() => onPrintFileDownload(printFileDownloadUrl)}
                      className="custom-print-photo-dl-btn"
                      btnSize="xs"
                    >
                      Download Hi&ndash;Res
                    </Button>
                  </div>
                </div>
              </div>
              {otherPrintablePurchasedCustomPrints.length > 0 && (
                <div className="row">
                  <div className="col-12 mb-2">
                    Other prints purchased by this user in this transaction:
                  </div>
                  {otherPrintablePurchasedCustomPrints.map(
                    otherPurchasedCustomPrint => {
                      const otherAccountCardPrintSize =
                        getPurchasedCustomPrintAccountCardPrintSize(
                          otherPurchasedCustomPrint,
                        );
                      return (
                        <div
                          key={otherPurchasedCustomPrint.id}
                          className="col-lg-4 col-6 mb-2"
                        >
                          <div className="photo-grid-card custom-print-photo">
                            {otherAccountCardPrintSize && (
                              <div className="custom-print-photo-size-tag">
                                {otherAccountCardPrintSize}
                              </div>
                            )}
                            <a
                              href="#"
                              onClick={event => {
                                event.preventDefault();
                                // Since the intent here is to view, we do not use the download URL
                                openModal(PHOTO_VIEW_MODAL, {
                                  photoUrl:
                                    otherPurchasedCustomPrint.print_file_url,
                                });
                              }}
                            >
                              <img
                                src={otherPurchasedCustomPrint.print_file_url}
                                alt=""
                              />
                            </a>

                            <Button
                              onClick={() =>
                                onPrintFileDownload(
                                  otherPurchasedCustomPrint.print_file_download_url,
                                )
                              }
                              className="custom-print-photo-dl-btn"
                              btnSize="xs"
                            >
                              Download Hi&ndash;Res
                            </Button>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              )}
            </Fragment>
          )}
          {!printFileDownloadUrl && (
            <Fragment>
              <h5 className="mb-1">User Selected Photos</h5>
              {purchasedCustomPrint?.order_status ===
              ORDER_STATUS.CUSTOMER_INPUT ? (
                <p>
                  User has yet to select their images. Once they do, the order
                  will be automatically marked as pending.
                </p>
              ) : (
                <Fragment>
                  <p>
                    Provided photos are in the order the user has decided to
                    have them placed.{' '}
                    {purchasedCustomPrint?.preview_image?.url && (
                      <Fragment>
                        Please be sure to check the{' '}
                        <Button
                          btnStyle="link"
                          onClick={() =>
                            openModal(PHOTO_VIEW_MODAL, {
                              photoUrl:
                                purchasedCustomPrint?.preview_image?.url,
                            })
                          }
                        >
                          preview image
                        </Button>{' '}
                        to see the order in which these pictures should appear
                        in.{' '}
                      </Fragment>
                    )}
                    You can click on the photos for a larger preview &amp;
                    download.
                  </p>
                  <div className="row">
                    {orderedPhotos.map(photoItem => {
                      const {
                        photo,
                        asset_image: assetImage,
                        position,
                      } = photoItem;
                      const url = getPurchasedCustomPrintPhotoUrl(photoItem);
                      const btnClasses = classNames({
                        'custom-print-photo-dl-btn': true,
                        'px-3': !!photo?.foreground?.original_url,
                      });

                      return (
                        <div
                          key={photo?.id ?? assetImage?.id}
                          className="col-lg-4 col-6 mb-2"
                        >
                          <div className="photo-grid-card custom-print-photo">
                            <a
                              href="#"
                              onClick={event => {
                                event.preventDefault();

                                openModal(PHOTO_VIEW_MODAL, {
                                  photo,
                                  photoUrl: assetImage?.url,
                                });
                              }}
                            >
                              <img src={url} alt="" />
                            </a>
                            <div className="custom-print-photo-number">
                              {position + 1}
                            </div>
                            <DownloadPhotoButtonContainer
                              photoId={photo?.id ?? assetImage?.id}
                              photoSize={IMAGE_SIZE_CODE.LARGE2X}
                              className={btnClasses}
                              btnSize="xs"
                            >
                              Download Hi&ndash;Res
                            </DownloadPhotoButtonContainer>
                            {!!photo?.foreground?.original_url && (
                              <DownloadPhotoButtonContainer
                                photoId={photo?.id ?? assetImage?.id}
                                photoSize={IMAGE_SIZE_CODE.LARGE2X}
                                className="custom-print-photo-foreground-dl-btn"
                                downloadPhotoForeground
                                btnStyle="white"
                                btnSize="xs"
                              >
                                Download Knock&ndash;out&nbsp;
                              </DownloadPhotoButtonContainer>
                            )}{' '}
                          </div>
                          {photo?.original_filename && (
                            <div className="text-xs mt-1">
                              {photo.original_filename}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Fragment>
              )}
            </Fragment>
          )}

          {isAlbumCustomPrintConfigurableInPlace(purchasedCustomPrint) &&
            purchasedCustomPrint?.order_status !==
              ORDER_STATUS.CUSTOMER_INPUT &&
            orderedPhotos?.length > 0 && (
              <Fragment>
                <h5 className="mb-1">Template Configuration</h5>
                <p>Here&rsquo;s how the user configured their template:</p>

                <CustomPrintPlaqueConfigurator
                  albumCustomPrint={purchasedCustomPrint}
                  className="custom-print-review-plaque-configurator"
                  identity={identity}
                  initialConfiguration={mapPurchasedCustomPrintToConfiguration(
                    purchasedCustomPrint,
                  )}
                  viewOnly
                />
              </Fragment>
            )}
        </div>
      </div>

      <PhotoViewModal>
        {photo => {
          if (!photo?.id) {
            return (
              <div className="mt-1 text-sm text-center text-white">
                Right click / Save as to download Hi-Res
              </div>
            );
          }

          return (
            <div className="mt-1">
              <DownloadPhotoButtonContainer
                photoId={photo.id}
                photoSize={IMAGE_SIZE_CODE.LARGE2X}
                btnBlock
              >
                Download Hi-Res Photo
              </DownloadPhotoButtonContainer>
            </div>
          );
        }}
      </PhotoViewModal>
      <PurchasedCustomPrintUpdateStatusModal
        purchasedCustomPrint={purchasedCustomPrint}
      />
    </Fragment>
  );
};

AlbumPurchasedCustomPrintDetailsContainer.propTypes = propTypes;

const withPurchasedCustomPrint = graphql(PurchasedCustomPrint, {
  options: ({ match, purchasedCustomPrintId }) => ({
    variables: {
      id: purchasedCustomPrintId || match.params.orderId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { purchasedCustomPrint, loading } }) => ({
    loading,
    purchasedCustomPrint,
  }),
});

const mapDispatchToProps = {
  openModal: openModalAction,
};

export default compose(
  withBasename,
  withRouter,
  connect(null, mapDispatchToProps),
  withPurchasedCustomPrint,
)(AlbumPurchasedCustomPrintDetailsContainer);
